<template>
	<div class="o-carSingle__step2">

		<button
			type="button"
			class="a-readMore u-fw550"
			:aria-disabled="store.isSubmitting ? 'true' : 'false'"
			@click="handleBack"
		>
			<svg width="12" height="10" viewBox="0 0 12 10" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
                <path d="M5.29688 0.78125L1.07812 5M1.07812 5L5.29688 9.21875M1.07812 5H10.9219" stroke="#1E1E2F" stroke-width="1.40625" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <span>Terug</span>
		</button>

		<div class="o-carSingle__step2Content">
			<div class="o-carSingle__step2Summary u-shadow">

				<button
					type="button"
					class="o-carSingle__step2SummaryToggle"
					:aria-expanded="isSummaryOpen ? 'true' : 'false'"
					:aria-controls="summaryContentId"
					@click="isSummaryOpen = ! isSummaryOpen"
				>
					<span>Bekijk je voertuig</span>
					<svg width="13" height="7" viewBox="0 0 13 7" fill="none" xmlns="http://www.w3.org/2000/svg">
						<path d="M5.50908 6.74502C5.85088 7.08682 6.40596 7.08682 6.74775 6.74502L11.9978 1.49502C12.3396 1.15322 12.3396 0.598145 11.9978 0.256348C11.656 -0.0854492 11.1009 -0.0854492 10.7591 0.256348L6.12705 4.88838L1.49502 0.259082C1.15322 -0.0827151 0.598144 -0.0827151 0.256348 0.259082C-0.0854492 0.600879 -0.0854492 1.15596 0.256348 1.49775L5.50635 6.74775L5.50908 6.74502Z" fill="#283344"/>
					</svg>
				</button>

				<div
					class="o-carSingle__step2SummaryContent"
					:id="summaryContentId"
					:data-expanded="isSummaryOpen ? 'true' : 'false'"
				>

					<div class="o-carSingle__step2SummaryHeader">
					<div class="o-carSingle__step2SummaryThumb">
						<img v-if="store.carImageSummaryUrl" :src="store.carImageSummaryUrl" alt="" />
						</div>
						<h3 class="o-carSingle__step2SummaryTitle">{{ store.carTitle }}</h3>
					</div>

					<div class="o-carSingle__step2Grid">
						<div>
							<span class="o-carSingle__infoLabel">Aanvraag</span>
							<strong>{{ submissionModeLabel }}</strong>
						</div>
					</div>

					<div class="o-carSingle__step2Grid" v-if="isLeaseMode">
						<div>
							<span class="o-carSingle__infoLabel">Looptijd
								<InfoPopup label="Looptijd">
									<p>{{ infoCopy.looptijd }}</p>
								</InfoPopup>
							</span>
							<strong>{{ store.looptijd }} maanden</strong>
						</div>
						<div>
							<span class="o-carSingle__infoLabel">Aanbetaling
								<InfoPopup label="Aanbetaling">
									<p>{{ infoCopy.aanbetaling }}</p>
								</InfoPopup>
							</span>
							<strong>{{ formatCurrency( store.aanbetaling || 0 ) }}</strong>
						</div>
						<div>
							<span class="o-carSingle__infoLabel">Slottermijn
								<InfoPopup label="Slottermijn">
									<p>{{ infoCopy.slottermijn }}</p>
								</InfoPopup>
							</span>
							<strong>{{ formatCurrency( store.slottermijn || 0 ) }}</strong>
						</div>
						<div>
							<span class="o-carSingle__infoLabel">Maandbedrag
								<InfoPopup label="Maandbedrag">
									<p>{{ infoCopy.maandbedrag }}</p>
								</InfoPopup>
							</span>
							<strong>{{ store.formattedMaandbedrag }}</strong>
						</div>
					</div>
					<div class="o-carSingle__step2Grid" v-else>
						<div>
							<span class="o-carSingle__infoLabel">Aanschafprijs
								<InfoPopup label="Aanschafprijs">
								</InfoPopup>
							</span>
							<strong>{{ formatCurrency( store.aankoopbedrag || 0 ) }}</strong>
						</div>
						<div v-if="store.btwBedrag">
							<span class="o-carSingle__infoLabel">Btw-bedrag</span>
							<strong>{{ formatCurrency( store.btwBedrag ) }}</strong>
						</div>
					</div>

				</div>

			</div>

			<div class="o-carSingle__step2Form">
				<div
					v-if="store.isSubmitting"
					class="o-carSingle__loaderOverlay"
					role="status"
					aria-live="polite"
					aria-busy="true"
				>
					<span class="u-visuallyHidden">Bezig met verzenden…</span>
					<Loader />
				</div>

				<form @submit.prevent="onSubmit" novalidate>
					<div class="o-carSingle__formField">
						<label for="companyName">Bedrijfsnaam<span aria-hidden="true">*</span></label>
						<input
							id="companyName"
							class="u-textInput"
							type="text"
							v-model="companyName"
							placeholder="Bedrijfsnaam"
							aria-required="true"
							:aria-invalid="fieldError('companyName') ? 'true' : 'false'"
							:aria-describedby="fieldError('companyName') ? 'companyName-error' : null"
							@blur="onFieldBlur('companyName')"
						/>
						<p v-if="fieldError('companyName')" :id="'companyName-error'" class="u-error">{{ fieldError('companyName') }}</p>
					</div>

					<div class="o-carSingle__formField">
						<label for="fullName">Naam<span aria-hidden="true">*</span></label>
						<input
							id="fullName"
							class="u-textInput"
							type="text"
							v-model="fullName"
							placeholder="Voor en achternaam"
							aria-required="true"
							:aria-invalid="fieldError('fullName') ? 'true' : 'false'"
							:aria-describedby="fieldError('fullName') ? 'fullName-error' : null"
							@blur="onFieldBlur('fullName')"
						/>
						<p v-if="fieldError('fullName')" :id="'fullName-error'" class="u-error">{{ fieldError('fullName') }}</p>
					</div>

					<div class="o-carSingle__formField">
						<label for="email">E-mailadres<span aria-hidden="true">*</span></label>
						<input
							id="email"
							class="u-textInput"
							type="email"
							inputmode="email"
							aria-required="true"
							v-model="email"
							placeholder="naam@email.nl"
							:aria-invalid="fieldError('email') ? 'true' : 'false'"
							:aria-describedby="fieldError('email') ? 'email-error' : null"
							@blur="onFieldBlur('email')"
						/>
						<p v-if="fieldError('email')" :id="'email-error'" class="u-error">{{ fieldError('email') }}</p>
					</div>

					<div class="o-carSingle__formField">
						<label for="phone">Telefoonnummer<span aria-hidden="true">*</span></label>
						<input
							id="phone"
							class="u-textInput"
							type="tel"
							inputmode="tel"
							v-model="phone"
							placeholder="06 12345678"
							aria-required="true"
							:aria-invalid="fieldError('phone') ? 'true' : 'false'"
							:aria-describedby="fieldError('phone') ? 'phone-error' : null"
							@blur="onFieldBlur('phone')"
						/>
						<p v-if="fieldError('phone')" :id="'phone-error'" class="u-error">{{ fieldError('phone') }}</p>
					</div>

					<div class="o-carSingle__formField">
						<SwitchReveal
							:model-value="inruilen"
							:plate="kenteken"
							:input-mode="kentekenInputMode"
							label="Wil je je huidige auto inruilen?"
							aria-label="Schakel inruilen in of uit"
							aria-label-plate="Kenteken van je inruilauto"
							country="NL"
							:announce="true"
							@update:model-value="onInruilenUpdate"
							@update:plate="onKentekenUpdate"
							@update:input-mode="onKentekenInputModeUpdate"
						/>
					</div>
					<div class="o-carSingle__step2Usps" v-if="formUsp">
						<Usp :label="formUsp" />
					</div>

					<FormNavigation
						:show-next="false"
						:show-submit="true"
						:can-proceed="canProceed"
						:is-submitting="store.isSubmitting"
						@submit="onSubmit"
					/>

				</form>

				<div class="o-carSingle__rating u-bgBlue">

					<div class="o-carSingle__ratingTop">

						<div class="o-carSingle__ratingIcons">
							<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
								<path d="M20 0H0V20H20V0Z" fill="#31A9E1"/>
								<path d="M10.0001 13.4792L13.0417 12.7083L14.3126 16.625L10.0001 13.4792ZM17.0001 8.41666H11.6459L10.0001 3.37499L8.35425 8.41666H3.00008L7.33341 11.5417L5.68758 16.5833L10.0209 13.4583L12.6876 11.5417L17.0001 8.41666Z" fill="white"/>
							</svg>
							<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
								<path d="M20 0H0V20H20V0Z" fill="#31A9E1"/>
								<path d="M10.0001 13.4792L13.0417 12.7083L14.3126 16.625L10.0001 13.4792ZM17.0001 8.41666H11.6459L10.0001 3.37499L8.35425 8.41666H3.00008L7.33341 11.5417L5.68758 16.5833L10.0209 13.4583L12.6876 11.5417L17.0001 8.41666Z" fill="white"/>
							</svg>
							<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
								<path d="M20 0H0V20H20V0Z" fill="#31A9E1"/>
								<path d="M10.0001 13.4792L13.0417 12.7083L14.3126 16.625L10.0001 13.4792ZM17.0001 8.41666H11.6459L10.0001 3.37499L8.35425 8.41666H3.00008L7.33341 11.5417L5.68758 16.5833L10.0209 13.4583L12.6876 11.5417L17.0001 8.41666Z" fill="white"/>
							</svg>
							<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
								<path d="M20 0H0V20H20V0Z" fill="#31A9E1"/>
								<path d="M10.0001 13.4792L13.0417 12.7083L14.3126 16.625L10.0001 13.4792ZM17.0001 8.41666H11.6459L10.0001 3.37499L8.35425 8.41666H3.00008L7.33341 11.5417L5.68758 16.5833L10.0209 13.4583L12.6876 11.5417L17.0001 8.41666Z" fill="white"/>
							</svg>
							<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
								<path d="M20 0H0V20H20V0Z" fill="#31A9E1"/>
								<path d="M10.0001 13.4792L13.0417 12.7083L14.3126 16.625L10.0001 13.4792ZM17.0001 8.41666H11.6459L10.0001 3.37499L8.35425 8.41666H3.00008L7.33341 11.5417L5.68758 16.5833L10.0209 13.4583L12.6876 11.5417L17.0001 8.41666Z" fill="white"/>
							</svg>
						</div>

						<span class="u-fw550" v-if="ratingText">{{ ratingText }}</span>

					</div>

				</div>
			</div>
		</div>
	</div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref, nextTick, inject } from 'vue';
import { useRouter } from 'vue-router';
import { useFormStore } from '../stores/form.js';
import FormNavigation from '../components/FormNavigation.vue';
import Usp from '../components/Usp.vue';
import InfoPopup from '../components/InfoPopup.vue';
import Loader from '../components/Loader.vue';
import SwitchReveal from '../components/SwitchReveal.vue';
import { getInfoCopy } from '../content/info.js';

const router = useRouter();
const store = useFormStore();

const copyConfig = inject( 'copyConfig', {} );
const infoCopy = computed( () => getInfoCopy( copyConfig ) );

const formUsp = computed( () => {
	return copyConfig && typeof copyConfig === 'object' && copyConfig.api_formulier_usp
		? copyConfig.api_formulier_usp
		: '';
} );

const ratingText = computed( () => {
	const beoordeling = copyConfig && typeof copyConfig === 'object' && copyConfig.beoordeling
		? copyConfig.beoordeling
		: '';
	if ( ! beoordeling ) {
		return '';
	}
	return `Klanten beoordelen ons met ${ beoordeling }`;
} );

const isSummaryOpen = ref( false );
const summaryContentId = 'step2-summary';

const hasAttemptedSubmit = ref( false );
const touchedFields = ref( {} );

const companyName = computed( {
	get: () => store.bedrijfsnaam,
	set: ( v ) => store.updateField( 'bedrijfsnaam', v ),
} );

const fullName = computed( {
	get: () => store.naam,
	set: ( v ) => store.updateField( 'naam', v ),
} );

const email = computed( {
	get: () => store.email,
	set: ( v ) => store.updateField( 'email', v ),
} );

const phone = computed( {
	get: () => store.telefoonnummer,
	set: ( v ) => store.updateField( 'telefoonnummer', v ),
} );

const inruilen = computed( {
	get: () => store.inruilen,
	set: ( v ) => store.updateCalculation( { inruilen: v } ),
} );

const kenteken = computed( {
	get: () => store.kenteken,
	set: ( v ) => store.updateCalculation( { kenteken: v } ),
} );

const kentekenInputMode = computed( {
	get: () => store.kentekenInputMode,
	set: ( v ) => store.updateCalculation( { kentekenInputMode: v } ),
} );

const onInruilenUpdate = ( value ) => {
	store.updateCalculation( { inruilen: value } );
};

const onKentekenUpdate = ( value ) => {
	store.updateCalculation( { kenteken: value } );
};

const onKentekenInputModeUpdate = ( value ) => {
	store.updateCalculation( { kentekenInputMode: value } );
};

const canProceed = computed( () => {
	return store.isStep2Valid && ! store.isSubmitting;
} );

const isLeaseMode = computed( () => store.submissionMode !== 'koop' );
const submissionModeLabel = computed( () => ( store.submissionMode === 'koop' ? 'Koop' : 'Lease' ) );

const formatCurrency = ( value ) => {
	if ( value === null || value === undefined ) {
		return '';
	}
	const numValue = Number( value );
	const isWholeNumber = Number.isInteger( numValue );
	return new Intl.NumberFormat( 'nl-NL', {
		style: 'currency',
		currency: 'EUR',
		minimumFractionDigits: isWholeNumber ? 0 : 2,
		maximumFractionDigits: isWholeNumber ? 0 : 2,
	} ).format( numValue );
};

const isRealImage = computed( () => {
	const url = store.carImageSummaryUrl || '';
	return url && url.indexOf( 'placeholder-car' ) === -1;
} );

const handleBack = () => {
	router.push( '/step/1' );
};

const onFieldBlur = ( fieldKey ) => {
	touchedFields.value[ fieldKey ] = true;
};

const fieldError = ( key ) => {
	// Always show server errors
	if ( store.errors && store.errors[ key ] ) {
		return store.errors[ key ];
	}
	// Show client-side validation errors if field is touched OR submit was attempted
	if ( touchedFields.value[ key ] || hasAttemptedSubmit.value ) {
		const clientErrors = store.step2ValidationErrors || {};
		return clientErrors[ key ] ? clientErrors[ key ] : '';
	}
	return '';
};

const focusFirstError = async () => {
	await nextTick();
	const order = [ 'companyName', 'fullName', 'email', 'phone' ];
	for ( const id of order ) {
		if ( fieldError( id ) ) {
			const el = document.getElementById( id );
			if ( el ) {
				el.focus();
			}
			break;
		}
	}
};

const onSubmit = async () => {
	hasAttemptedSubmit.value = true;
	store.isSubmitting = true;
	store.errors = {};
	const ok = await store.submit();
	if ( ! ok ) {
		await focusFirstError();
	}
	// If successful, redirect happens in store.submit() via window.location.href
};

onMounted( () => {
	document.body.classList.add( 'is-step-2' );
} );

onUnmounted( () => {
	document.body.classList.remove( 'is-step-2' );
} );
</script>
